import * as Sentry from '@sentry/react'
import axios, { AxiosError, AxiosResponse } from 'axios'

import { dispatchUnauthorizedEvent } from 'contexts/auth'
import { isAxiosResponseError } from 'utils/api'
import { removeQueryParamsEmptyKeys } from 'utils/general'

function isUnparsedGetResponse(
  response: AxiosResponse,
): response is AxiosResponse<string> {
  return response.config.method === 'get' && typeof response.data === 'string'
}

function rejectUnparsedResponse(response: AxiosResponse<string>) {
  const error = new AxiosError(
    'Response body was not valid JSON',
    AxiosError.ERR_BAD_RESPONSE,
    response.config,
    response.request,
    response,
  )

  Sentry.captureException(error, {
    extra: {
      bodyLength: response.data.length,
      contentType: response.headers['content-type'],
      status: response.status,
      url: response.config.url,
    },
  })

  return Promise.reject(error)
}

axios.interceptors.request.use((config) => {
  // If we have request parameters, we want to strip out any empty ones.
  if (config.params) {
    config.params = removeQueryParamsEmptyKeys(config.params)
  }

  return config
})

axios.interceptors.response.use(
  (response) => {
    // Axios returns an empty, truncated, or HTML body as a raw string instead of
    // failing, and code expecting an array or object would then crash on it.
    if (isUnparsedGetResponse(response)) {
      return rejectUnparsedResponse(response)
    }

    return response
  },
  (error) => {
    if (isAxiosResponseError(error)) {
      // If we ever get back an Unauthorized API response code, we should log the user out.
      if (error.response?.status === 401) {
        dispatchUnauthorizedEvent()
      }
    }

    return Promise.reject(error)
  },
)
